import styled, { css } from 'styled-components';
import { Rail } from './VideoRail/styles';

export const DesktopStage = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    height: 100dvh;
    background-color: ${theme.co.blobBackground};
  `,
);

export const ProgressRow = styled.div(
  ({ theme }) => css`
    flex-shrink: 0;
    width: 100%;
    padding: ${theme.sz.s3} ${theme.sz.s8} ${theme.sz.s4};

    @media (max-width: 64rem) {
      padding-inline: ${theme.sz.s6};
    }
  `,
);

export const Layout = styled.div`
  display: grid;
  flex: 1;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  min-height: 0;
`;

export const MediaColumn = styled.div`
  position: relative;
  min-height: 0;
`;

export const ContentColumn = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow-y: auto;
    padding: ${theme.sz.s6} ${theme.sz.s8} ${theme.sz.s8} ${theme.sz.s12};

    @media (max-width: 64rem) {
      padding-inline: ${theme.sz.s6};
    }
  `,
);

export const CardArea = styled.div(
  ({ theme }) => css`
    display: flex;
    flex: 1;
    align-items: center;
    width: 100%;
    max-width: ${theme.sz.ms};
    padding-bottom: ${theme.sz.s16};
  `,
);

/** Full-width content when there is no video or audio to put in a left column */
export const ContentOnlyLayout = styled.div(
  ({ theme }) => css`
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    overflow-y: auto;
    padding: ${theme.sz.s6} ${theme.sz.s8};
  `,
);

export const ContentOnlyCard = styled.div(
  ({ theme }) => css`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    gap: ${theme.sz.s4};
    padding-bottom: ${theme.sz.s16};
  `,
);

export const ContentOnlyArea = styled.div(
  ({ theme }) => css`
    width: 100%;
    max-width: ${theme.sz.ms};
  `,
);

export const MobileStage = styled.div<{ $hasMedia: boolean }>(
  ({ theme, $hasMedia }) => css`
    position: relative;
    height: 100dvh;
    overflow: hidden;
    background-color: ${$hasMedia ? theme.co.neutral0 : theme.co.blobBackground};

    /* Centres the play button in the part of the video the card content doesn't cover */
    [data-testid='playButton'] {
      height: 70%;
    }
  `,
);

export const MobileNoMedia = styled.div(
  ({ theme }) => css`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    height: 100%;
    padding: calc(${theme.sz.s20} + env(safe-area-inset-top)) ${theme.sz.s4}
      calc(${theme.sz.s4} + env(safe-area-inset-bottom));
  `,
);

export const MobileOverlay = styled.div(
  ({ theme }) => css`
    position: absolute;
    inset: 0;
    z-index: ${theme.zi.z2};
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: calc(${theme.sz.s20} + env(safe-area-inset-top)) ${theme.sz.s4}
      calc(${theme.sz.s4} + env(safe-area-inset-bottom));
    background: linear-gradient(transparent 35%, hsla(0, 0%, 0%, 0.55) 65%, hsla(0, 0%, 0%, 0.8));
    pointer-events: none;
  `,
);

export const MobileCardBody = styled.div<{ $railBeside: boolean }>(
  ({ theme, $railBeside }) => css`
    display: grid;
    gap: ${theme.sz.s4};
    pointer-events: auto;

    ${
      $railBeside
        ? css`
            grid-template-columns: minmax(0, 1fr) auto;
            grid-template-areas: 'content rail';
            align-items: end;
          `
        : css`
            grid-template-areas:
              'rail'
              'content';
          `
    }
  `,
);

export const MobileRail = styled.div(
  ({ theme }) => css`
    grid-area: rail;
    justify-self: end;
    min-height: 0;

    @media (max-height: 44rem) {
      ${Rail} {
        gap: ${theme.sz.s2};
      }
    }
  `,
);

export const MobileContent = styled.div`
  grid-area: content;
  min-width: 0;
`;

export const SavedLayout = styled.div(
  ({ theme }) => css`
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    overflow-y: auto;
    padding: ${theme.sz.s6} ${theme.sz.s8};
  `,
);

export const SavedCard = styled.div(
  ({ theme }) => css`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    gap: ${theme.sz.s4};
    padding-bottom: ${theme.sz.s16};
  `,
);

export const SavedMedia = styled.div(
  ({ theme }) => css`
    position: relative;
    flex-shrink: 0;
    width: min(${theme.sz.xs}, 28vw);
    aspect-ratio: 1;
    overflow: hidden;
    border-radius: ${theme.bo.mediumLarge};
  `,
);

export const SavedContent = styled.div(
  ({ theme }) => css`
    width: 100%;
    max-width: ${theme.sz.sm};
    margin-inline-start: ${theme.sz.s6};
  `,
);

export const PagerSlot = styled.div(
  ({ theme }) => css`
    position: absolute;
    inset-inline: 0;
    bottom: ${theme.sz.s6};
    z-index: ${theme.zi.z2};
    display: flex;
    justify-content: center;
    pointer-events: none;

    > * {
      pointer-events: auto;
    }
  `,
);

export const MobilePagerSlot = styled(PagerSlot)(
  ({ theme }) => css`
    top: calc(${theme.sz.s20} + env(safe-area-inset-top));
    bottom: auto;
    z-index: ${theme.zi.z3};
  `,
);
